import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import { User } from '../types';
import io from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
console.log('SocketContext: SOCKET_URL =', SOCKET_URL);

interface SocketContextType {
  socket: any;
  isConnected: boolean;
  emit: (event: string, ...args: any[]) => void;
  on: (event: string, callback: (...args: any[]) => void) => void;
  off: (event: string, callback?: (...args: any[]) => void) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

const SocketProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<any>(null);
  
  // Initialize with no authentication - will be updated when auth is available
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  
  // Listen for auth state changes through a custom event
  useEffect(() => {
    const handleAuthChange = (event: CustomEvent) => {
      setIsAuthenticated(event.detail.isAuthenticated);
      setUser(event.detail.user);
    };
    
    window.addEventListener('authStateChange', handleAuthChange as EventListener);
    
    return () => {
      window.removeEventListener('authStateChange', handleAuthChange as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    // Create socket connection if it doesn't exist
    if (!socketRef.current) {
      const socket = io(SOCKET_URL, {
        transports: ['websocket'],
        auth: {
          token: ''
        },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000
      });

      socket.on('connect', () => {
        console.log('SocketContext: Connected to socket server');
        socket.emit('join', user._id);
        setIsConnected(true);
        console.log('SocketContext: User joined room:', user._id);
      });

      socket.on('disconnect', () => {
        console.log('SocketContext: Disconnected from socket server');
        setIsConnected(false);
      });

      socket.on('reconnect', () => {
        console.log('SocketContext: Reconnected to socket server');
        socket.emit('join', user._id);
        setIsConnected(true);
      });

      socket.on('error', (error) => {
        console.error('SocketContext: Socket connection error:', error);
        setIsConnected(false);
      });

      // Handle pong response
      socket.on('pong', () => {
        console.log('SocketContext: Received pong from server');
      });

      // Add heartbeat to keep connection alive
      const heartbeat = setInterval(() => {
        if (socket.connected) {
          socket.emit('ping');
        }
      }, 30000); // Send ping every 30 seconds

      socketRef.current = socket;

      return () => {
        clearInterval(heartbeat);
      };
    }

    return () => {
      // Don't disconnect on cleanup to maintain connection across navigation
      // Only disconnect when user logs out or component unmounts
    };
  }, [isAuthenticated, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  const value = {
    socket: socketRef.current,
    isConnected,
    emit: (event: string, ...args: any[]) => {
      if (socketRef.current) {
        socketRef.current.emit(event, ...args);
      }
    },
    on: (event: string, callback: (...args: any[]) => void) => {
      if (socketRef.current) {
        socketRef.current.on(event, callback);
      }
    },
    off: (event: string, callback?: (...args: any[]) => void) => {
      if (socketRef.current) {
        socketRef.current.off(event, callback);
      }
    },
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <SocketProviderInner>{children}</SocketProviderInner>;
}; 